import { Period } from '@restworld/utility-types';

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const SORTED_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const formatTime = (time?: string): string | null => {
  if (!time) return null;
  const splitTime = time.match(/.{1,2}/g);
  return splitTime ? splitTime.join(':') : null;
};

const formatTimeInterval = (
  openTime: string | null,
  closeTime: string | null,
  anotherOpenTime?: string | null,
  anotherCloseTime?: string | null
): string => {
  if (anotherOpenTime && anotherCloseTime) {
    return `${openTime} - ${closeTime} | ${anotherOpenTime} - ${anotherCloseTime}`;
  }
  return `${openTime} - ${closeTime}`;
};

const formatDayPeriods = (periods: Period[]): Record<string, string[]> => {
  try {
    return (
      typeof periods === 'string'
        ? (JSON.parse((periods as unknown as string).replace(/'/g, '"')) as Period[])
        : periods
    ).reduce((acc, { open, close }) => {
      const day = DAYS[open.day];
      const openTime = open?.time ? formatTime(open.time) : '';
      const closeTime = close?.time ? formatTime(close.time) : '';
      const anotherOpenTime = open?.anotherTime ? formatTime(open.anotherTime) : '';
      const anotherCloseTime = close?.anotherTime ? formatTime(close.anotherTime) : '';

      const timeInterval = formatTimeInterval(openTime, closeTime, anotherOpenTime, anotherCloseTime);

      if (!acc[day]) acc[day] = [];
      acc[day].push(timeInterval);

      return acc;
    }, {} as Record<string, string[]>);
  } catch (error) {
    console.error('Error in formatting google opening hours', error);
    return {};
  }
};

// Google Places represents "open 24 hours, 7 days a week" with a single period
// that has `open.day === 0`, `open.time === '0000'` and no `close` field.
// We detect that shape explicitly, otherwise the reduce would only place a
// (malformed) entry under Sunday and every other day would render as "Closed".
const is24x7 = (periods: Period[]): boolean =>
  periods.length === 1 && periods[0]?.open?.day === 0 && periods[0]?.open?.time === '0000' && !periods[0]?.close;

export const formatGoogleOpeningHours = (periods: Period[]): string[] => {
  if (!periods?.length) return [];

  try {
    if (is24x7(periods)) {
      return SORTED_DAYS.map((day) => `${day}: 00:00 - 23:59`);
    }

    const formattedHours = formatDayPeriods(periods);

    return SORTED_DAYS.map((day) => {
      const intervals = formattedHours[day]?.join(' | ') || 'Closed';
      return `${day}: ${intervals}`;
    });
  } catch (error) {
    console.error('Error in formatting google opening hours', error);
    return [];
  }
};
